import React, { useEffect, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import { useSelector, useDispatch } from 'react-redux';
import JsPDF from 'jspdf';

import { SomaIcon, SomaButton, SomaTooltip } from '@soma/react';
import { saveAs } from 'file-saver';
import DOMPurify from 'dompurify';
import Button from 'xpi-black-ui/Base/Button';
import Bullet from 'xpi-black-ui/Base/Bullet';
import Loading from 'xpi-black-ui/Base/Loading';
import { Row, Col } from 'xpi-black-ui/Base/Grid';
import KeyValue from 'xpi-black-ui/Common/KeyValue';
import ContentBox from 'xpi-black-ui/Base/ContentBox';
import { P, Text } from 'xpi-black-ui/Base/Typography';
import ContentDivider from 'xpi-black-ui/Common/ContentDivider';
import Table, { Thead, Th, Td, Tr, Tbody } from 'xpi-black-ui/Common/Table';
import BlackContainer from 'components/Common/BlackContainer';
import { LoadProduct } from 'redux-flow/actions/product';
import html2canvas from 'html2canvas';
import Badge from '../../components/Utils/Badge';

import Informations from './compose/Informations';
import OtherOperations from './compose/OtherOperations';
import Disclaimer from './compose/Informations/Disclaimer';
import Print from './compose/Print';

import { useCMSModal } from '../../hooks/useCMSModal';

import Brand from '../../components/Common/Brand';
import GoBack from '../../components/Common/GoBack';
import PageView from '../../components/Common/PageView';
import Clients from '../../components/Feature/Clients/SendClientProposals';

import { aboutStrategy } from './texts';
import { getPushOperation } from './service';

import waConfig, { waDataReplace } from '../../utils/waConfig';


import Page, {
  StructureIndex,
  GoBackStyles,
  ProductContent,
  StructureItem,
  Profitability,
  Introduction,
  Scenarios,
  ScenariosTable,
  ScenariosDesciption,
} from './styles';

export default (props) => {
  const dispatch = useDispatch();
  const cmsModal = useCMSModal();

  const { match } = props;
  const { product, loading } = useSelector(state => state.Product);
  const [isLoadingBlade, setIsLoadingBlade] = useState(false);
  const [showClients, setShowClients] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [hasProduct, setHasProduct] = useState(false);
  const [productID, setProductID] = useState(() => {
    try {
      const { params } = match;
      const { id } = params;

      const regx = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
      if (regx.test(id)) {
        return id;
      }
      return Number(id);
    } catch (error) {
      return -1;
    }
  });

  const getPerformanceColor = (performance) => {
    if (performance > 0) {
      return '#0A9E5A';
    }
    if (performance < 0) {
      return '#CC3333';
    }
    return '#E0AD00';
  };

  useEffect(() => {
    dispatch(LoadProduct(productID));
  }, [dispatch]);

  useEffect(() => {
    if (!loading && product) {
      const prevProductID = product.codigoOperacao;

      if (prevProductID === productID) {
        setHasProduct(true);

        if (!loading && hasProduct) {
          if (product.sobCustodia) cmsModal.openInCustodyModal();
          if (product.estrutura === 'POP' || product.estrutura === 'POP Plus' || product.estrutura === 'POP Plus Sob Custodia' || product.estrutura === 'POP Sob Custodia') cmsModal.openInStructurePopModal();
          // if (product.estrutura === 'POP Plus') cmsModal.openInStructurePopModal();
          // if (product.estrutura === 'POP Plus Sob Custodia') cmsModal.openInStructurePopModal();
          // if (product.estrutura === 'POP Sob Custodia') cmsModal.openInStructurePopModal();
        }
      }
    }
  }, [product, hasProduct, loading]);


  const exportProposalToPDF = async (operationId, quantity) => {
    setIsLoadingBlade(true);

    try {
      const response = await getPushOperation(operationId, quantity);
      const COMMENT_PSEUDO_COMMENT_OR_LT_BANG = new RegExp(
        '<!--[\\s\\S]*?(?:-->)?'
        + '<!---+>?' // A comment with no body
        + '|<!(?![dD][oO][cC][tT][yY][pP][eE]|\\[CDATA\\[)[^>]*>?'
        + '|<[?][^>]*>?', // A pseudo-comment
        'g',
      );

      const sanitizedData = DOMPurify.sanitize(response.data);
      const el = document.createElement('html');
      el.innerHTML = sanitizedData
        .replaceAll('\n', '')
        .replaceAll('`', '')
        .replace(COMMENT_PSEUDO_COMMENT_OR_LT_BANG, '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/>\s+</g, '><');

      const target = document.getElementById('print');

      const style = document.createElement('style');
      style.textContent = "*, *::before, *::after { text-rendering: optimizeLegibility; -webkit-box-sizing: border-box; -moz-box-sizing: border-box; box-sizing: border-box; -moz-osx-font-smoothing: grayscale; -webkit-font-smoothing: antialiased; } html, body { font-family: 'Roboto', sans-serif; font-family: 'Roboto Slab', serif; scroll-behavior: smooth; margin: 0; padding: 0; height: 100%; width: 100%; color: white; } .footer { padding: 3rem 0; width: 100%; background: linear-gradient(0deg, #050B17, #050B17), #DCDCDC; } @media print { .footer { position: fixed; bottom: 0; } }";
      document.head.append(style);

      const content = el.querySelectorAll('body > *');

      content.forEach((items) => {
        target.append(items);
      });

      html2canvas(document.getElementById('print'), {
        imageTimeout: 2000,
        logging: true,
      }).then((canvas) => {
        const image = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');

        const pdfSize = {
          width: document.querySelector('#print').clientWidth,
          height: document.querySelector('#print').clientHeight,
        };

        const pdf = new JsPDF({
          orientation: 'p', // landscape
          unit: 'pt', // points, pixels won't work properly
          format: [pdfSize.width, pdfSize.height], // set needed dimensions for any element
        });

        pdf.addImage(image, 'PNG', 0, 0, pdfSize.width, pdfSize.height);
        return pdf.output('blob');
      }).then(async (output) => {
        await saveAs(output, `LÂMINA-${operationId}.pdf`);
        setIsLoadingBlade(false);
        target.innerHTML = '';
      });
    } catch {
      toastr.error('Não foi possível fazer o download da lâmina', 'Tente novamente mais tarde');
      setIsLoadingBlade(false);
    }
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, [match]);

  useEffect(() => {
    const { params } = match;
    const isDebugMode = params?.debugMode === 'debugMode';
    setIsDebugMode(isDebugMode);
  }, []);

  const possuiCotacao = product.cotacao && product.cotacao > 1;

  const infos = [
    { title: 'Data de encerramento', value: product.vencimentoFormatado },
    { title: 'Preço referência', value: `${product.metodoCotacao} ${product.precoReferenciaFormatado} ${product?.unidadeMedida ? `/ ${product?.precoUnidadeMedida}` : ''}` },
    { title: 'Unidade de negociação', value: `${product?.multiplicadorFormatado} ${product?.unidadeMedida ? ` ${product?.unidadeMedida}` : ''}` },
    { title: 'Cotação US$', value: `${product?.cotacaoFormatado} / dólar`, hide: !possuiCotacao },
    { title: (
      <SomaTooltip
        inverse
        position="top"
        style={{ display: 'flex' }}
        aria-label="Preço da operação"
        label={product.tooltipPrecoOperecao}
      >
        <Text>Preço da operação</Text>
        <SomaIcon icon="info" size="sm" color="#FFC709" className="icon-info" />
      </SomaTooltip>
    ),
    value: product.ativo === 'IDI' ? product.precoOperacaoFormatado : `${product.precoOperacaoPercentual} (${product.precoOperacaoFormatado})`,
    },
    { title: (
      <SomaTooltip
        inverse
        position="top"
        style={{ display: 'flex' }}
        aria-label="Comissão a dividir"
        label={product?.tooltipComissaDividir}
      >
        <Text>Comissão a dividir</Text>
        <SomaIcon icon="info" size="sm" color="#FFC709" className="icon-info" />
      </SomaTooltip>
    ),
    value: product.ativo === 'IDI' ? product.comissaoFormatado : `${product.comissaoPercentual} (${product.comissaoFormatado})`,
    },
    { title: 'Lote mínimo', value: `${product.loteMinimoFormatado} quantidade(s)` },
    { title: 'Limite disponível', value: product.limiteFormatado },
    { title: 'Valor do termo', value: `${product.termoFormatado} %`, hide: !product.ehTermo },
  ];

  const getEnableAcquisition = () => {
    if (isDebugMode) {
      return false;
    }
    return !product.limite > 0;
  };

  return (
    <>
      <BlackContainer>
        <Page>
          {loading && !hasProduct && <Loading fixed text="Carregando..." />}
          {isLoadingBlade && <Loading fixed text="Baixando Lâmina..." />}
          {!loading && hasProduct && (
            <>
              <PageView page={waDataReplace(waConfig.pageview.product, `${product.ativo}/${product.codigoOperacao}`)} />
              <ProductContent>
                <>
                  <GoBackStyles>
                    <GoBack />
                  </GoBackStyles>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {product?.isHighlight && <Badge value="Destaque" bgColor="#FFC709" txtColor="black" txtFontWeight="bold" />}
                    {product?.tipoEstrategia && <Badge value={product?.tipoEstrategia} />}
                  </div>

                  <Row className="header" align="flex-start" justifyContent="space-between">
                    <Col>
                      <Row align="flex-start">
                        <Col>
                          <Brand src={product.imagem} name={product.ativo} className="logo" style={{ marginRight: '10px' }} />
                        </Col>
                        <Col>
                          <P className="name">
                            {product.ativo}
                            {product?.descricaoAtivo || product?.bolsaNegociacao ? ' -' : ''}
                            {' '}
                            {product?.descricaoAtivo || ''}
                            {' '}
                            {product?.bolsaNegociacao || ''}
                          </P>
                          <P className="structure">{product?.estrutura}</P>
                        </Col>
                      </Row>
                    </Col>
                    <Col className="buttons">
                      <SomaButton
                        inverse
                        size="sm"
                        icon="download"
                        modifier="secondary"
                        aria-label="Baixar lâmina"
                        data-wa={waConfig.event.product.export}
                        onClick={() => exportProposalToPDF(product.codigoOperacao, product?.loteMinimo)}
                        disabled={isLoadingBlade}
                      >
                        Baixar lâmina
                      </SomaButton>
                      <Button
                        disabled={getEnableAcquisition()}
                        onClick={() => setShowClients(true)}
                        data-wa={waConfig.event.product.buttonSendProposals}
                      >
                        Enviar Proposta
                      </Button>
                    </Col>
                  </Row>
                </>
                <ContentBox>
                  <Row justifyContent="space-between">
                    {infos.filter(info => !info.hide).map(info => (
                      <KeyValue
                        column
                        key={info.value}
                        title={info.title}
                        value={info.value}
                      />
                    ))}
                  </Row>
                </ContentBox>

                <ContentBox>
                  <P bigger bold className="title">Estrutura</P>
                  <P style={{ marginBottom: '24px' }}>Composição da estrutura.</P>
                  {product.pernas.map((perna, index) => (
                    <StructureItem key={index + 1}>
                      <P style={{ marginBottom: '0' }}>
                        <StructureIndex>{index + 1}</StructureIndex>
                        {perna.natureza}
                        {' '}
                        {perna.ratio > 1 ? `${perna.ratio}x` : ''}
                        {' '}
                        {perna.tipoEstrutura}

                        {product.ativo === 'IDI' ? (
                          <>
                            {perna.strike > 0 && perna.tipoEstrutura !== 'da ação' && ` - Strike ${perna.strikeFormattedIDI}`}
                            {perna.barreira && ` - ${perna.barreira} ${perna.barrierFormattedIDI}`}
                            {perna.rebate > 0 && ` - Rebate ${perna.formattedRebate}`}
                            {perna.tipoEstrutura !== 'da ação' && ` / Taxa Pré-Equivalente: ${perna.preEquivalentRateFormattedIDI}`}
                          </>
                        ) : (
                          <>
                            {perna.strike > 0 && perna.tipoEstrutura !== 'da ação' && ` - Strike ${perna.strikeFormatado}`}
                            {perna.barreira && ` - ${perna.barreira} ${perna.barreiraFormatado}`}
                            {perna.rebate > 0 && ` - Rebate ${perna.formattedRebate}`}
                          </>
                        )}

                      </P>
                    </StructureItem>
                  ))}
                </ContentBox>
                <ContentBox>
                  <Profitability>
                    <P bigger bold className="title">Rentabilidade</P>
                    <P style={{ marginBottom: '24px' }}>Valores referentes a cada unidade contratada.</P>
                    <Row>
                      <ContentDivider>
                        <KeyValue
                          column
                          title={(
                            <SomaTooltip
                              inverse
                              position="top"
                              aria-label="Lucro máximo unitário"
                              style={{ display: 'flex' }}
                              label="Lucro máximo por unidade contratada."
                            >
                              <Text>Lucro máximo unitário</Text>
                              <SomaIcon icon="info" size="sm" color="#FFC709" style={{ marginLeft: '4px', bottom: '-3px' }} />
                            </SomaTooltip>
                          )}
                          value={(
                            <Row style={{ marginLeft: '-2px' }}>
                              <SomaIcon size="sm" icon="arrow-up" color="#0A9E5A" />
                              <P className="value">{product?.lucroMaximoFormatado}</P>
                            </Row>
                          )}
                        />
                      </ContentDivider>
                      <ContentDivider>
                        <KeyValue
                          column
                          title={(
                            <SomaTooltip
                              inverse
                              position="top"
                              aria-label="Prejuízo máximo unitário"
                              style={{ display: 'flex' }}
                              label="Prejuízo máximo por unidade contratada."
                            >
                              <Text>Prejuízo máximo unitário</Text>
                              <SomaIcon icon="info" size="sm" color="#FFC709" style={{ marginLeft: '4px', bottom: '-3px' }} />
                            </SomaTooltip>
                          )}
                          value={(
                            <Row style={{ marginLeft: '-2px' }}>
                              <SomaIcon size="sm" icon="arrow-down" color="#cc3333" />
                              <P className="value">{product?.prejuizoMaximoFormatado}</P>
                            </Row>
                          )}
                        />
                      </ContentDivider>
                      {product?.temRebate && (
                        <ContentDivider>
                          <KeyValue
                            column
                            title={(
                              <SomaTooltip
                                inverse
                                position="top"
                                aria-label="Rebate"
                                style={{ display: 'flex' }}
                                label="Valor pago ao detentor da opção em caso de atingimento de barreira."
                              >
                                <Text>Rebate</Text>
                                <SomaIcon icon="info" size="sm" color="#FFC709" style={{ marginLeft: '4px', bottom: '-3px' }} />
                              </SomaTooltip>
                            )}
                            value={(
                              <Row>
                                <SomaIcon size="sm" icon="dollar-sign" color="#0A9E5A" />
                                <P className="value">
                                  {`${product?.rebateFormatado} (${product?.rebatePercentual})`}
                                </P>
                              </Row>
                            )}
                          />
                        </ContentDivider>
                      )}
                    </Row>
                  </Profitability>

                  <Introduction>
                    <P bigger bold className="title margin-bottom">Introdução</P>
                    {aboutStrategy(product.estrutura) && (
                      <KeyValue
                        column
                        title="Entenda a estratégia em 1 minuto"
                        value={aboutStrategy(product.estrutura)}
                      />
                    )}
                    {product.objetivo && <KeyValue column title="Objetivo" value={product.objetivo} />}
                    {product.execucao && <KeyValue column title="Execução" value={product.execucao} />}
                  </Introduction>
                </ContentBox>
                <ContentBox>
                  <>
                    <P bigger bold className="title margin-bottom">Cenários de retorno no vencimento</P>
                    <Scenarios>
                      <ScenariosTable>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>{product.ativo === 'IDI' ? 'CDI - Taxa média do período' : `Variação % de ${product.ativo}`}</Th>
                              <Th>{product.ativo === 'IDI' ? 'Retorno por unidade (R$)' : 'Variação % da estratégia'}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {product.cenarios.length > 0 && product.cenarios.map(scenario => (
                              <Tr key={scenario.performance}>
                                <Td>
                                  <Bullet color={getPerformanceColor(scenario.variacao)}>
                                    <Text style={{ fontSize: '14px', color: 'inherit' }}>{scenario.variacaoFormatado}</Text>
                                  </Bullet>
                                </Td>
                                <Td>
                                  <Bullet color={getPerformanceColor(scenario.payoff)}>
                                    <Text style={{ fontSize: '14px', color: 'inherit' }}>{scenario.payoffFormatado}</Text>
                                  </Bullet>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </ScenariosTable>
                      {product.cenariosVencimento.length > 0
                      && (
                      <ScenariosDesciption>
                        {product.cenariosVencimento.map(scenario => (
                          <KeyValue
                            column
                            key={scenario.texto}
                            value={scenario.texto}
                            title={(
                              <Bullet color="gray">
                                <Text>{scenario.titulo}</Text>
                              </Bullet>
                            )}
                          />
                        ))}
                      </ScenariosDesciption>
                      )
                      }
                    </Scenarios>
                  </>
                </ContentBox>
                <Informations product={product} />
                <OtherOperations />
                <Disclaimer />
              </ProductContent>
              {showClients
                && (
                <Clients
                  operation={product}
                  assetName={product.ativo}
                  commission={product.comissao}
                  onCloseDialog={() => setShowClients(false)}
                  isRubiYield={false}
                />
                )
              }
            </>
          )}
        </Page>
      </BlackContainer>
      <Print />
    </>
  );
};
